/**
 * El terminal como cosa que se autoriza, no sólo como columna de auditoría.
 *
 * Una tablet no es un detalle del inicio de sesión: es una llave física que
 * se puede perder, prestar o robar, y que guarda la sesión abierta durante
 * semanas para que el técnico no tenga que teclear su contraseña en cada
 * centro. Eso obliga a poder quitarle la llave a UN aparato sin echar a su
 * dueño ni cambiar contraseñas.
 *
 * El estado vive en `device.status` desde la primera migración. Lo que no
 * existía es que sirviera para algo: se consultaba al sincronizar y en
 * ningún sitio más, así que un terminal marcado como revocado seguía
 * entrando y renovando su sesión indefinidamente.
 */

export const DEVICE_STATUSES = ['PENDIENTE', 'ACTIVE', 'READONLY', 'LOCKED', 'REVOKED'] as const;

export type DeviceStatus = (typeof DEVICE_STATUSES)[number];

export interface DeviceAccess {
  readonly allowed: boolean;
  /**
   * Por qué no, para enseñarlo tal cual en la tablet.
   *
   * Un «no autorizado» a secas hace que el técnico reinstale la aplicación,
   * pruebe otra contraseña y acabe llamando por teléfono. Decirle que su
   * terminal está esperando aprobación le dice además a quién llamar.
   */
  readonly reason?: string;
  /** Puede entrar, pero no debe poder enviar cambios. */
  readonly readOnly: boolean;
}

/**
 * ¿Puede este terminal abrir sesión?
 *
 * `READONLY` sí entra: es un terminal degradado, no expulsado. El técnico
 * tiene que poder consultar su jornada y leer lo que ya descargó aunque no
 * se le deje escribir; echarlo perdería el trabajo pendiente de enviar que
 * tenga en la tablet, que es exactamente lo contrario de lo que interesa.
 */
export function canDeviceSignIn(status: DeviceStatus): DeviceAccess {
  switch (status) {
    case 'ACTIVE':
      return { allowed: true, readOnly: false };

    case 'READONLY':
      return { allowed: true, readOnly: true };

    case 'PENDIENTE':
      return {
        allowed: false,
        readOnly: true,
        reason:
          'Este terminal está esperando autorización. Avise a la oficina para que lo apruebe; ' +
          'no hace falta que vuelva a instalar nada.',
      };

    case 'LOCKED':
      return {
        allowed: false,
        readOnly: true,
        reason: 'Este terminal está bloqueado. Avise a la oficina.',
      };

    case 'REVOKED':
      return {
        allowed: false,
        readOnly: true,
        reason: 'Este terminal ha sido dado de baja y ya no puede acceder.',
      };
  }
}

/**
 * Estado con el que se registra un terminal que nadie ha visto antes.
 *
 * Es una constante y no un literal suelto porque tiene que ser el mismo en
 * el alta, en la pantalla del panel y en las pruebas. El día que se decida
 * que ciertos terminales entran directos, se cambia aquí y no en cinco
 * sitios de los que cuatro se olvidan.
 */
export const DEVICE_INITIAL_STATUS: DeviceStatus = 'PENDIENTE';

/** ¿Está esperando que alguien lo apruebe? */
export function isAwaitingApproval(status: DeviceStatus): boolean {
  return status === 'PENDIENTE';
}
